export const VALUE_SELLING_GLOBAL_RULES = {
  priority: [
    'productivity',
    'ROI_per_hectare',
    'efficiency',
    'risk_reduction',
    'crop_stability',
    'quality',
    'nutrient_use_efficiency'
  ],
  avoid: ['price_only', 'commodity_logic', 'discount_first', 'product_without_diagnosis'],
  reasoningModel: ['SPIN', 'Challenger', 'OPC', 'ValueSelling', 'AgronomicDiagnosis']
} as const;

export const ROI_SCENARIO_MULTIPLIERS = {
  conservador: 0.6,
  moderado: 1.0,
  agressivo: 1.3
} as const;

export const YARA_LINE_ROLE_MAP = {
  YaraAmplix: { role: 'estabilidade e eficiência', use: 'lavouras de alto risco / estresse / abertura de safra' },
  YaraVita: { role: 'ajuste fino e teto produtivo', use: 'fases críticas, florada e enchimento' },
  YaraMila: { role: 'base de alta eficiência', use: 'plantio e adubação de base' },
  YaraTera: { role: 'precisão máxima', use: 'fertirrigação, irrigado, HF, café, citrus' },
  YaraLiva: { role: 'qualidade e vigor', use: 'culturas de alto padrão e qualidade final' },
  YaraBela: { role: 'eficiência de nitrogênio', use: 'fase vegetativa com alta demanda de N' },
  YaraVera: { role: 'fonte de nitrogênio', use: 'suporte de N em sistemas de alta resposta' },
  YaraRega: { role: 'NPK para fertirrigação', use: 'manejo em sistemas irrigados' }
} as const;

export const YARA_LINE_GAIN_PERCENT: Record<string, { min: number; max: number }> = {
  YaraAmplix: { min: 3, max: 8 },
  YaraVita: { min: 4, max: 10 },
  YaraMila: { min: 5, max: 12 },
  YaraLiva: { min: 3, max: 7 },
  YaraTera: { min: 6, max: 15 },
  YaraBela: { min: 3, max: 6 },
  YaraVera: { min: 2, max: 5 },
  YaraRega: { min: 4, max: 10 }
};

export const CROP_BENCHMARKS = {
  soja: { avgYield: 65, pricePerBag: 120, unit: 'sc/ha' },
  milho: { avgYield: 180, pricePerBag: 60, unit: 'sc/ha' },
  cafe: { avgYield: 35, pricePerBag: 900, unit: 'sc/ha' },
  cana: { avgYield: 90, pricePerBag: 120, unit: 't/ha (referência convertida)' },
  pecuaria: { avgYield: 18, pricePerBag: 320, unit: '@/ha (referência convertida)' },
  geral: { avgYield: 60, pricePerBag: 120, unit: 'unidade/ha (referência)' }
} as const;

export const YARA_CROP_FOCUS: Record<string, string[]> = {
  soja: ['YaraMila', 'YaraVita', 'YaraAmplix'],
  milho: ['YaraMila', 'YaraBela', 'YaraVita', 'YaraAmplix'],
  cafe: ['YaraLiva', 'YaraTera', 'YaraVita'],
  citrus: ['YaraLiva', 'YaraTera', 'YaraVita'],
  cana: ['YaraMila', 'YaraVita'],
  hortalicas: ['YaraLiva', 'YaraTera', 'YaraVita'],
  geral: ['YaraMila', 'YaraVita', 'YaraAmplix']
};

export const YARA_PROBLEM_TO_LINES: Array<{ regex: RegExp; lines: string[] }> = [
  { regex: /baixa produtividade|baixo rendimento|pouca saca|baixa resposta/, lines: ['YaraMila', 'YaraVita', 'YaraAmplix'] },
  { regex: /estresse|seca|veranico|clima/, lines: ['YaraAmplix', 'YaraVita'] },
  { regex: /baixa qualidade|padrao ruim|qualidade final/, lines: ['YaraLiva', 'YaraVita'] },
  { regex: /variabilidade|desuniforme|desuniformidade/, lines: ['YaraAmplix', 'YaraTera'] },
  { regex: /falta de controle|baixa precisao|fertirrigacao/, lines: ['YaraTera', 'YaraRega'] },
  { regex: /enraizamento|raiz fraca|arranque ruim/, lines: ['YaraAmplix'] },
  { regex: /florada|pegamento/, lines: ['YaraVita'] },
  { regex: /enchimento|grao chocho|mal enchimento/, lines: ['YaraVita', 'YaraLiva'] },
  { regex: /perda de nitrogenio|volatilizacao|nitrogenio/, lines: ['YaraBela', 'YaraMila', 'YaraVera'] }
];

export const YARA_STAGE_TO_LINES: Array<{ regex: RegExp; lines: string[] }> = [
  { regex: /plantio|abertura de safra|base/, lines: ['YaraMila', 'YaraAmplix'] },
  { regex: /vegetativo/, lines: ['YaraVita', 'YaraAmplix', 'YaraBela'] },
  { regex: /reprodutivo|florada|enchimento|graos/, lines: ['YaraVita', 'YaraLiva'] },
  { regex: /fertirrigacao|pivo|irrigado/, lines: ['YaraTera', 'YaraRega'] }
];

export const YARA_OBJECTIVE_TO_LINES: Array<{ regex: RegExp; lines: string[] }> = [
  { regex: /aumentar produtividade|mais sacas|teto produtivo/, lines: ['YaraMila', 'YaraVita', 'YaraAmplix'] },
  { regex: /reduzir risco|estabilidade|previsibilidade/, lines: ['YaraAmplix', 'YaraTera'] },
  { regex: /melhorar qualidade|padrao comercial|valor de venda/, lines: ['YaraLiva', 'YaraVita'] },
  { regex: /precisao|controle fino/, lines: ['YaraTera', 'YaraRega'] },
  { regex: /eficiencia|retorno por hectare|roi/, lines: ['YaraMila', 'YaraBela', 'YaraVera'] }
];
